"use client";

import React from "react";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, House as Home, Layers, ShieldCheck, Umbrella, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const residentialWorkList = [{
  text: "Roof repairs and re-tiling projects"
}, {
  text: "Single and multi-storey extensions"
}, {
  text: "Chimney rebuilds, repointing, and cowls"
}, {
  text: "Exterior rendering and wall maintenance"
}, {
  text: "Fascia, soffit, and gutter replacements"
}];
const commercialWorkList = [{
  text: "Town-centre retail and office developments"
}, {
  text: "Live, occupied commercial sites with public protection"
}, {
  text: "Phased new build construction schemes"
}, {
  text: "Refurbishment and structural repair access"
}, {
  text: "Pedestrian gantries and fan protection systems"
}];
const tradeAccessCards = [{
  title: "Roofing Scaffolding",
  description: "Stable working platforms with hoist arms and loading bays for tile and slate handling.",
  to: "/RoofingScaffoldingPage",
  icon: Home
}, {
  title: "Temporary Roofs",
  description: "Weatherproof tin-hat coverings to protect ongoing renovations from rain and wind.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge Protection",
  description: "Heavy-duty handrails and toe boards ensuring fall-prevention compliance on high decks.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck
}];
export default function WokingServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="wokingscaffoldingservices" data-nav="light" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 border-border text-foreground">
            Woking & Surrey Access Solutions
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl font-default">
            Scaffolding services in Woking
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From town-centre commercial properties and office developments to residential
            refurbishments, we erect safe, compliant scaffolding structures across Woking and the
            surrounding Surrey boroughs.
          </p>
        </div>

        {/* Two Main Subsections: Residential and Commercial */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Subsection 1: Residential Scaffolding */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                      <Home className="size-6" />
                    </div>
                    <Badge variant="secondary">Domestic & Residential</Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold font-default">
                    Residential scaffolding in Woking
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground">
                    Tailored access structures for residential properties, designed with care for
                    gardens, conservatories, and neighbourhood access.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-2.5">
                    <p className="text-sm font-semibold text-foreground uppercase tracking-wider">
                      Common residential projects:
                    </p>
                    <ul className="space-y-2">
                      {residentialWorkList.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item.text}</span>
                        </li>)}
                    </ul>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">Explore residential access:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/ResidentialScaffoldingPage" className="inline-flex items-center gap-1">
                          Residential Scaffolding
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/ChimneyScaffoldingPage" className="inline-flex items-center gap-1">
                          Chimney Scaffolding
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="pt-2">
                <Button asChild className="w-full">
                  <Link to="/ContactRequestQuotePage">Request residential quote</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Subsection 2: Commercial Scaffolding */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between">
              <div>
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-lg bg-primary/10 text-primary w-fit">
                      <Building2 className="size-6" />
                    </div>
                    <Badge variant="secondary">Commercial & Industrial</Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold font-default">
                    Commercial scaffolding in Woking
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground">
                    High-specification scaffolding engineered for main contractors, commercial
                    developers, and busy town-centre properties.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-2.5">
                    <p className="text-sm font-semibold text-foreground uppercase tracking-wider">
                      Commercial applications:
                    </p>
                    <ul className="space-y-2">
                      {commercialWorkList.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item.text}</span>
                        </li>)}
                    </ul>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">Explore commercial access:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/CommercialScaffoldingPage" className="inline-flex items-center gap-1">
                          Commercial Scaffolding
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/MainContractorsDevelopersPage" className="inline-flex items-center gap-1">
                          Main Contractors & Developers
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="pt-2">
                <Button asChild className="w-full">
                  <Link to="/ContactRequestQuotePage">Discuss commercial project</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>

        {/* Roofing & Trade Access Block */}
        <div className="rounded-2xl border border-border bg-muted/40 p-6 md:p-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-primary font-medium text-sm mb-2">
                <Layers className="size-4" />
                <span>Specialist Trades Support</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                Roofing & trade access in Woking
              </h3>
              <p className="text-muted-foreground mt-2 text-sm md:text-base">
                Dedicated solutions built for roofers, solar installers, and façade specialists
                requiring safe access at height.
              </p>
            </div>
            <Button variant="outline" asChild className="shrink-0">
              <Link to="/RoofingContractorsPage">
                <span>View trade solutions</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tradeAccessCards.map((item, i) => {
            const IconComponent = item.icon;
            return <div key={i} data-index={i} className="rounded-xl border border-border bg-card p-6 flex flex-col justify-between hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                  <div className="space-y-3">
                    <div className="p-2.5 rounded-md bg-accent text-foreground w-fit">
                      <IconComponent className="size-5 text-primary" />
                    </div>
                    <h4 className="text-lg font-bold font-default text-foreground">
                      {item.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  <div className="pt-5 mt-4 border-t border-border">
                    <Button variant="link" size="sm" asChild className="px-0">
                      <Link to={item.to} className="inline-flex items-center gap-1.5 font-semibold">
                        <span>Learn more</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Accreditations Footer Strip */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Wrench className="size-4" />
            </div>
            <p className="text-sm text-muted-foreground">
              Confirmed accreditations held by BMF Scaffolding Ltd:
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">NASC</Badge>
            <Badge variant="outline">CHAS</Badge>
            <Badge variant="secondary">Full Public Liability</Badge>
          </div>
        </div>
      </div>
    </section>;
}